<template>
  <!--
    The instance's mark: the company logo (the menu) or the installed app's
    icon (the sign-in screen), in a box that is its final size from the first
    frame — so nothing beside it moves when it arrives.

    Three states, never the wrong brand in between: a skeleton while nothing
    is known (first visit) or while the picture is on its way; the picture,
    faded in only if a skeleton was actually seen (a cached one is just
    there); the initials when there is no picture or it failed to load.
  -->
  <span
    class="brand-mark"
    :class="{ 'brand-mark--plate': plate }"
    :style="{ '--brand-mark-size': `${size}px`, '--brand-mark-radius': radius }"
    aria-hidden="true"
  >
    <UiSkeleton
      v-if="showSkeleton"
      class="brand-mark__skeleton"
      width="100%"
      height="100%"
    />
    <img
      v-if="picture"
      :key="picture"
      :ref="watchImage"
      :src="picture"
      alt=""
      class="brand-mark__img"
      :class="{ 'is-loaded': loaded, 'is-instant': instant, 'is-decoded': decoded }"
      decoding="async"
      @error="failed = picture"
    />
    <span v-else-if="!view.pending" class="brand-mark__initials">{{ view.initials }}</span>
  </span>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { UiSkeleton } from '@general/ui-kit'

import { useBrandView } from '../brand'

const props = defineProps({
  // logo: the company logo, as uploaded · icon: the installed app's icon
  kind: { type: String, default: 'logo' },
  size: { type: Number, default: 32 },
  radius: { type: String, default: 'var(--radius-sm)' },
  // A white plate under the picture: a logo is drawn for paper.
  plate: { type: Boolean, default: false },
})

const view = useBrandView(props.kind)

// The URL that failed, not a flag: a new version of the logo gets its chance.
const failed = ref(null)
const picture = computed(() => (view.value.src && view.value.src !== failed.value ? view.value.src : null))

const loaded = ref(false)
const instant = ref(false)
const decoded = ref(false)
// The skeleton stays under the picture until the fade is over: a cross-fade,
// not a fade from an empty hole.
const covered = ref(false)
const FADE_MS = 220
let fadeTimer = null
watch(picture, () => {
  clearTimeout(fadeTimer)
  loaded.value = false
  instant.value = false
  decoded.value = false
  covered.value = false
  watched = null
})
onBeforeUnmount(() => clearTimeout(fadeTimer))

const showSkeleton = computed(() => view.value.pending || (Boolean(picture.value) && !covered.value))

// Under this, a picture that was on its way is not worth a fade.
const INSTANT_MS = 80
let watched = null

/**
 * When to show the picture: once it is DECODED, not merely downloaded. A
 * complete image still undecoded paints nothing for a frame or two, and on the
 * white plate that was a white square before the logo.
 *
 * A picture the browser already holds — the inline script in index.html asks
 * for it while the app's code is still downloading — is shown from the first
 * frame with no fade: a reload must not blink. The plate and the end of the
 * skeleton wait for the decode all the same.
 */
function watchImage(element) {
  if (!element || element === watched) return
  watched = element
  const mountedAt = performance.now()
  if (element.complete && element.naturalWidth > 0) {
    loaded.value = true
    instant.value = true
  }
  const ready = typeof element.decode === 'function'
    ? element.decode()
    : new Promise((resolve, reject) => {
      if (element.complete && element.naturalWidth > 0) return resolve()
      element.addEventListener('load', resolve, { once: true })
      element.addEventListener('error', reject, { once: true })
    })
  // A picture that cannot be had is the error handler's: the initials.
  ready.then(() => { if (watched === element) reveal(performance.now() - mountedAt < INSTANT_MS) }, () => {})
}

function reveal(atOnce) {
  decoded.value = true
  if (atOnce || instant.value) {
    loaded.value = true
    instant.value = true
    covered.value = true
    return
  }
  loaded.value = true
  fadeTimer = setTimeout(() => { covered.value = true }, FADE_MS + 40)
}
</script>

<style scoped>
.brand-mark {
  position: relative;
  display: inline-grid;
  flex-shrink: 0;
  width: var(--brand-mark-size);
  height: var(--brand-mark-size);
  border-radius: var(--brand-mark-radius);
}

.brand-mark > * { grid-area: 1 / 1; }

.brand-mark__skeleton {
  position: absolute;
  inset: 0;
  border-radius: inherit;
}

.brand-mark__img {
  position: relative;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: inherit;
  opacity: 0;
  transition: opacity 220ms ease-out; /* FADE_MS */
}
.brand-mark--plate .brand-mark__img.is-decoded { background: #fff; }
.brand-mark__img.is-loaded { opacity: 1; }
.brand-mark__img.is-instant { transition: none; }

.brand-mark__initials {
  display: grid;
  place-items: center;
  border-radius: inherit;
  background: var(--color-primary);
  color: var(--color-text-on-primary);
  font-weight: 700;
  font-size: calc(var(--brand-mark-size) * 0.4);
  line-height: 1;
  letter-spacing: -0.02em;
}

@media (prefers-reduced-motion: reduce) {
  .brand-mark__img { transition: none; }
}
</style>
